import type { PerformanceMonitorService } from '../services/performance-monitor';

export function monitorSharedWorker(
  worker: SharedWorker | Worker,
  performanceMonitor: PerformanceMonitorService
) {
  performanceMonitor.mark('sharedWorker:init:start');

  const startTime = performance.now();
  let connectionTime = 0;
  let firstMessageTime = 0;

  const isSharedWorker = 'port' in worker;
  const port = isSharedWorker ? worker.port : worker;

  const originalPostMessage = port.postMessage.bind(port);

  const wrappedPostMessage = function (
    this: typeof port,
    ...args: Parameters<typeof port.postMessage>
  ) {
    if (connectionTime === 0) {
      connectionTime = performance.now() - startTime;
      performanceMonitor.mark('sharedWorker:connected');
    }

    return originalPostMessage.apply(this, args);
  };

  port.postMessage = wrappedPostMessage as typeof port.postMessage;

  const messageHandler = () => {
    if (firstMessageTime === 0) {
      firstMessageTime = performance.now() - startTime;
      performanceMonitor.mark('sharedWorker:firstMessage');
      performanceMonitor.measure(
        'sharedWorker:init:total',
        'sharedWorker:init:start',
        'sharedWorker:firstMessage'
      );

      performanceMonitor.setSharedWorkerMetrics({
        startTime,
        connectionTime,
        firstMessageTime,
        totalTime: firstMessageTime,
      });

      port.removeEventListener('message', messageHandler as EventListener);
    }
  };

  port.addEventListener('message', messageHandler as EventListener);

  if (isSharedWorker) {
    worker.port.start();
  }

  return worker;
}

export function monitorIndexedDB(
  performanceMonitor: PerformanceMonitorService
) {
  const originalOpen = indexedDB.open;
  let isFirstOpen = true;

  indexedDB.open = function (...args) {
    const dbName = args[0];
    const startTime = performance.now();

    performanceMonitor.mark(`indexedDB:${dbName}:open:start`);

    const request = originalOpen.apply(this, args);

    request.addEventListener('success', () => {
      const duration = performance.now() - startTime;
      performanceMonitor.mark(`indexedDB:${dbName}:open:success`);
      performanceMonitor.measure(
        `indexedDB:${dbName}:open`,
        `indexedDB:${dbName}:open:start`,
        `indexedDB:${dbName}:open:success`
      );

      if (isFirstOpen) {
        isFirstOpen = false;
        performanceMonitor.setIndexedDBMetrics({
          openTime: duration,
          firstQuery: duration,
        });
      }
    });

    return request;
  };
}
